import React, { memo, ReactElement, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import UserAvatar from "../UserAvatar";
import { Link, useHistory } from "react-router-dom";
import DropdownButton from "app/components/DropdownButton";
import SpaceMenu from "app/components/SpaceTabsContainer/SpaceMenu";
import DownArrow from "../../../public/images/down-arrow.svg";
import earth_icon from "app/images/earth.svg";
import usga_logo from "../../../public/usga_logo_big.png";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { hasFeature } from "app/utils/hasFeature";
import Navbar from "app/routes/Scheduling/Navbar";
import { useSelector } from "app/utils/hooks";
import { RootState } from "typedefs";
import { shallowEqual } from "react-redux";

interface PagePanelProps {
  panelClassName?: string;
  className?: string;
  headerWrapperClassName?: string;
  title?: string;
  subTitle?: string | React.ReactNode;
  avatar?: string;
  athleteId?: string;
  athleteExternalId?: string;
  email?: string;
  phone?: string;
  role?: string;
  titleStyles?: string;
  buttonType?: "primary" | "secondary" | "outline-secondary" | "upgrade";
  buttonTitle?: string | ReactElement;
  buttonStyles?: string;
  onButtonClick?: () => void;
  url?: string;
  headerChildren?: React.ReactNode;
  children?: React.ReactNode;
  leftButton?: React.ReactNode;
  timezone?: string;
}

const PagePanel = ({
  panelClassName,
  className,
  title,
  subTitle,
  avatar,
  athleteExternalId,
  email,
  phone,
  role,
  titleStyles,
  buttonType,
  buttonTitle,
  buttonStyles,
  onButtonClick,
  url,
  headerChildren,
  children,
  leftButton,
  timezone,
}: PagePanelProps) => {
  const athleteData = {
    athleteId: athleteExternalId,
    title,
    avatar,
    email,
    phone,
    role,
  };

  const isPublic = location.pathname.startsWith("/public");
  const history = useHistory();
  const [isUSGAIntegrationActive, setIntegration] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const athleteIdRef = useRef<string | null>(null);
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const showNavbar =
    location.pathname.includes("/calendar") && self?.kind !== "athlete";

  useEffect(() => {
    athleteIdRef.current = athleteExternalId ?? null;
  }, [athleteExternalId]);

  useEffect(() => {
    if (!athleteExternalId) {
      abortControllerRef.current?.abort();
      setIntegration(false);

      return undefined;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const thisFetchId = ++requestIdRef.current;
    const thisFetchAthleteId = athleteExternalId;

    const fetchAthleteIntegration = async () => {
      try {
        const response = await schedulingFetchGet(
          `/student_profiles/${thisFetchAthleteId}/integrations`,
          {},
          {},
          abortControllerRef.current.signal,
        );

        const integration = response.find(
          (integration) => integration.name === "ghin",
        );

        if (
          requestIdRef.current === thisFetchId &&
          athleteIdRef.current === thisFetchAthleteId
        ) {
          setIntegration(integration.status === "active");
        }
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Request was aborted.");
        } else {
          console.error(error);
        }

        if (
          requestIdRef.current === thisFetchId &&
          athleteIdRef.current === thisFetchAthleteId
        ) {
          setIntegration(false);
        }
      }
    };

    fetchAthleteIntegration();

    return () => abortControllerRef.current?.abort();
  }, [athleteExternalId]);

  return (
    <div className={cls(styles.booking_options_wrapper, className)}>
      {showNavbar && <Navbar />}

      <div className={cls(styles.container, panelClassName)}>
        {(title || buttonTitle || leftButton) && (
          <div className={styles.header_container}>
            {(title || subTitle || avatar) && (
              <div
                className={cls(styles.header_title_container, {
                  [styles.public_header_title_container]: isPublic,
                })}
              >
                {leftButton}
                {avatar && (
                  <UserAvatar
                    sizeHeight={64}
                    sizeWidth={64}
                    altName="avatar"
                    className={styles.header_logo}
                    srcName={avatar}
                    userName={title}
                  />
                )}
                <div className={styles.header_title_wrapper}>
                  {title && (
                    <div className={styles.title_container}>
                      {isPublic && (
                        <button
                          className={styles.back_button}
                          onClick={() => history.goBack()}
                        >
                          <i className="ico ico-back" />
                        </button>
                      )}
                      <h2 className={cls(styles.title, titleStyles)}>
                        {title}
                      </h2>
                      {isUSGAIntegrationActive && (
                        <div className={styles.usga_logo}>
                          <img src={usga_logo} alt="USGA logo" />
                        </div>
                      )}
                      {/* CN-2829: the menu is hidden for a space whose athlete holds a coach
                          account. It used to name the roles that may see it, so every other
                          value hid it too. A user who never picked a role keeps the default
                          kind "Unknown", which is not "athlete", so an athlete with real
                          bookings lost the menu. Name the one role we exclude instead. */}
                      {athleteExternalId && hasFeature(self?.features, "scheduling") && role !== "coach" && (
                        <DropdownButton
                          btnClassName={styles.dropdown_btn}
                          btnContent={
                            <img src={DownArrow} alt={"arrow-down"} />
                          }
                        >
                          <SpaceMenu
                            athleteData={athleteData}
                            isUSGAIntegrationActive={isUSGAIntegrationActive}
                          />
                        </DropdownButton>
                      )}
                    </div>
                  )}
                  {subTitle && <p className={styles.sub_title}>{subTitle}</p>}
                </div>
                {isPublic && timezone && (
                  <div className={styles.timezone_container}>
                    <img src={earth_icon} />
                    <span>{timezone}</span>
                  </div>
                )}
              </div>
            )}
            {buttonTitle && (
              <Link to={url ? url : "#"}>
                <button
                  className={cls("btn", styles.button, buttonStyles, {
                    ["btn-primary"]: buttonType === "primary",
                    ["btn-secondary"]: buttonType === "secondary",
                    ["btn-upgrade"]: buttonType === "upgrade",
                    ["btn-outline-secondary"]:
                      buttonType === "outline-secondary",
                  })}
                  onClick={onButtonClick}
                >
                  {buttonTitle}
                </button>
              </Link>
            )}
            {headerChildren}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default memo(PagePanel);
